import { Head, useForm, usePage } from '@inertiajs/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Layout from '@/layouts/Layout';
import { Heading } from '@/components/ui/heading';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { FlashBanner } from '@/components/ui/flash-banner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { logError } from '@/lib/logger';

type BillingProfile = {
  heading?: string | null;
  forwardEmail?: string | null;
  address?: string | null;
  sdi?: string | null;
  pec?: string | null;
  vatNumber?: string | null;
  fiscalCode?: string | null;
  updatedAt?: string | null;
  updatedByAdmin?: {
    id: number;
    fullName: string;
    email: string;
  } | null;
};

type InvoiceRow = {
  id: string;
  number?: string | null;
  status?: string | null;
  currency?: string | null;
  amount_paid: number;
  amount_due: number;
  created?: string | null;
  period_start?: string | null;
  period_end?: string | null;
  hosted_invoice_url?: string | null;
  invoice_pdf?: string | null;
};

type PageProps = {
  profile: BillingProfile;
  invoices: InvoiceRow[];
  pagination: {
    nextCursor?: string | null;
    hasMore: boolean;
  };
  errors?: Array<{ code?: string }>;
  validationErrors?: Record<string, string[]>;
};

type BillingFormPayload = {
  billing_heading: string;
  billing_forward_email: string;
  billing_address: string;
  billing_sdi: string;
  billing_pec: string;
  billing_vat_number: string;
  billing_fiscal_code: string;
};

const profileToForm = (profile: BillingProfile): BillingFormPayload => ({
  billing_heading: profile.heading ?? '',
  billing_forward_email: profile.forwardEmail ?? '',
  billing_address: profile.address ?? '',
  billing_sdi: profile.sdi ?? '',
  billing_pec: profile.pec ?? '',
  billing_vat_number: profile.vatNumber ?? '',
  billing_fiscal_code: profile.fiscalCode ?? '',
});

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatCurrency = (amountCents: number, currency?: string | null) => {
  const target = (currency || 'EUR').toUpperCase();
  try {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: target }).format(amountCents / 100);
  } catch {
    return `€${(amountCents / 100).toFixed(2)}`;
  }
};

const INVOICE_STATUS_LABELS: Record<string, string> = {
  paid: 'Pagata',
  open: 'Aperta',
  draft: 'Bozza',
  void: 'Annullata',
  uncollectible: 'Non riscossa',
};

const toDayKey = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10);
};

const invoiceStatusLabel = (status?: string | null) => {
  if (!status) return '—';
  return INVOICE_STATUS_LABELS[status] || status;
};

const invoiceStatusClassName = (status?: string | null) => {
  switch (status) {
    case 'paid':
      return 'border-0 bg-primary/15 text-primary';
    case 'open':
      return 'border-0 bg-warning/20 text-warning';
    case 'draft':
      return 'border-0 bg-muted text-muted-foreground';
    case 'void':
    case 'uncollectible':
      return 'border-0 bg-destructive/15 text-destructive';
    default:
      return 'border-0 bg-muted text-muted-foreground';
  }
};

const invoiceDisplayAmount = (invoice: InvoiceRow) => {
  if (invoice.amount_paid > 0) {
    return {
      value: invoice.amount_paid,
      hint: 'Pagato',
    };
  }

  return {
    value: invoice.amount_due,
    hint: invoice.amount_due > 0 ? 'Da pagare' : '—',
  };
};

const invoiceAction = (invoice: InvoiceRow) => {
  if (invoice.invoice_pdf) {
    return {
      label: 'Scarica PDF',
      url: invoice.invoice_pdf,
    };
  }

  if (invoice.hosted_invoice_url) {
    return {
      label: 'Apri fattura',
      url: invoice.hosted_invoice_url,
    };
  }

  return null;
};

const invoicePeriodHint = (invoice: InvoiceRow) => {
  if (!invoice.period_start || !invoice.period_end) return null;

  const periodStartKey = toDayKey(invoice.period_start);
  const periodEndKey = toDayKey(invoice.period_end);
  const createdKey = toDayKey(invoice.created);
  if (!periodStartKey || !periodEndKey) return null;

  const sameStartEnd = periodStartKey === periodEndKey;
  const sameAsCreated = createdKey && periodStartKey === createdKey && periodEndKey === createdKey;
  if (sameAsCreated) return null;

  if (sameStartEnd) {
    return `Competenza: ${formatDate(invoice.period_start)}`;
  }

  return `Competenza: ${formatDate(invoice.period_start)} → ${formatDate(invoice.period_end)}`;
};

export default function BillingShow() {
  const { props } = usePage<PageProps>();
  const { profile, invoices, pagination, errors, validationErrors } = props;

  const initialForm = useMemo(() => profileToForm(profile), [profile]);

  const billingForm = useForm<{ therapist: BillingFormPayload }>({
    therapist: initialForm,
  });

  const [invoiceRows, setInvoiceRows] = useState<InvoiceRow[]>(invoices);
  const [nextCursor, setNextCursor] = useState<string | null>(pagination.nextCursor ?? null);
  const [hasMore, setHasMore] = useState<boolean>(Boolean(pagination.hasMore));
  const [invoiceErrors, setInvoiceErrors] = useState<Array<{ code?: string }>>(errors ?? []);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  useEffect(() => {
    setInvoiceRows(invoices);
    setNextCursor(pagination.nextCursor ?? null);
    setHasMore(Boolean(pagination.hasMore));
    setInvoiceErrors(errors ?? []);
  }, [invoices, pagination.nextCursor, pagination.hasMore, errors]);

  const fieldError = useCallback(
    (field: keyof BillingFormPayload) => {
      const messages = validationErrors?.[field];
      return messages && messages.length > 0 ? messages[0] : null;
    },
    [validationErrors],
  );

  const updateField = useCallback(
    (field: keyof BillingFormPayload, value: string) => {
      billingForm.setData('therapist', {
        ...billingForm.data.therapist,
        [field]: value,
      });
    },
    [billingForm],
  );

  const loadMoreInvoices = useCallback(async () => {
    if (!nextCursor || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      const params = new URLSearchParams({ cursor: nextCursor });
      const response = await fetch(`/therapists/billing/invoices?${params.toString()}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
        credentials: 'same-origin',
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload) {
        setInvoiceErrors([ { code: 'stripe_invoice_unavailable' } ]);
        return;
      }

      const newInvoices = Array.isArray(payload.invoices) ? payload.invoices as InvoiceRow[] : [];
      setInvoiceRows((current) => [ ...current, ...newInvoices ]);
      setNextCursor(payload.page?.nextCursor ?? null);
      setHasMore(Boolean(payload.page?.hasMore));
      setInvoiceErrors(Array.isArray(payload.errors) ? payload.errors : []);
    } catch (error) {
      logError('Errore caricamento fatture', error);
      setInvoiceErrors([ { code: 'stripe_invoice_unavailable' } ]);
    } finally {
      setIsLoadingMore(false);
    }
  }, [nextCursor, isLoadingMore]);

  const hasValidationErrors = Boolean(validationErrors && Object.keys(validationErrors).length > 0);
  const lastUpdatedLabel = useMemo(() => {
    if (!profile.updatedAt) return 'Nessun salvataggio ancora';
    if (profile.updatedByAdmin) {
      return `Ultimo aggiornamento: ${new Date(profile.updatedAt).toLocaleString('it-IT')} · fatto da ${profile.updatedByAdmin.fullName}`;
    }
    return `Ultimo aggiornamento: ${new Date(profile.updatedAt).toLocaleString('it-IT')} · fatto da te`;
  }, [profile.updatedAt, profile.updatedByAdmin]);

  const invoicesSummary = useMemo(() => {
    return invoiceRows.reduce(
      (summary, invoice) => {
        summary.count += 1;
        summary.totalPaid += invoice.amount_paid;
        if (invoice.status !== 'paid') {
          summary.toPay += Math.max(invoice.amount_due, 0);
        }
        return summary;
      },
      {
        count: 0,
        totalPaid: 0,
        toPay: 0,
      },
    );
  }, [invoiceRows]);

  return (
    <Layout
      isAdmin
      mainHeader={
        <Heading level={2} wrapperClassName="mb-4">
          Fatturazione
        </Heading>
      }
    >
      <Head title="Fatturazione" />

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Dati per fatturazione</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {hasValidationErrors ? (
              <FlashBanner tone="error" message="Controlla i campi evidenziati e riprova." />
            ) : null}

            <form
              className="space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                billingForm.patch('/therapists/billing', { preserveScroll: true });
              }}
            >
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="billing_heading">Intestazione</Label>
                  <Input
                    id="billing_heading"
                    value={billingForm.data.therapist.billing_heading}
                    onChange={(event) => updateField('billing_heading', event.target.value)}
                  />
                  {fieldError('billing_heading') ? <p className="text-xs text-destructive">{fieldError('billing_heading')}</p> : null}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="billing_forward_email">Email inoltro (opzionale)</Label>
                  <Input
                    id="billing_forward_email"
                    type="email"
                    value={billingForm.data.therapist.billing_forward_email}
                    onChange={(event) => updateField('billing_forward_email', event.target.value)}
                  />
                  {fieldError('billing_forward_email') ? <p className="text-xs text-destructive">{fieldError('billing_forward_email')}</p> : null}
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="billing_address">Indirizzo</Label>
                  <Input
                    id="billing_address"
                    value={billingForm.data.therapist.billing_address}
                    onChange={(event) => updateField('billing_address', event.target.value)}
                  />
                  {fieldError('billing_address') ? <p className="text-xs text-destructive">{fieldError('billing_address')}</p> : null}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="billing_vat_number">Partita IVA</Label>
                  <Input
                    id="billing_vat_number"
                    value={billingForm.data.therapist.billing_vat_number}
                    onChange={(event) => updateField('billing_vat_number', event.target.value)}
                  />
                  {fieldError('billing_vat_number') ? <p className="text-xs text-destructive">{fieldError('billing_vat_number')}</p> : null}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="billing_fiscal_code">Codice fiscale</Label>
                  <Input
                    id="billing_fiscal_code"
                    value={billingForm.data.therapist.billing_fiscal_code}
                    onChange={(event) => updateField('billing_fiscal_code', event.target.value)}
                  />
                  {fieldError('billing_fiscal_code') ? <p className="text-xs text-destructive">{fieldError('billing_fiscal_code')}</p> : null}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="billing_sdi">SDI</Label>
                  <Input
                    id="billing_sdi"
                    value={billingForm.data.therapist.billing_sdi}
                    onChange={(event) => updateField('billing_sdi', event.target.value)}
                  />
                  {fieldError('billing_sdi') ? <p className="text-xs text-destructive">{fieldError('billing_sdi')}</p> : null}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="billing_pec">PEC</Label>
                  <Input
                    id="billing_pec"
                    type="email"
                    value={billingForm.data.therapist.billing_pec}
                    onChange={(event) => updateField('billing_pec', event.target.value)}
                  />
                  {fieldError('billing_pec') ? <p className="text-xs text-destructive">{fieldError('billing_pec')}</p> : null}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Button type="submit" disabled={billingForm.processing}>
                  {billingForm.processing ? 'Salvataggio...' : 'Salva modifiche'}
                </Button>
                <p className="text-xs text-muted-foreground">{lastUpdatedLabel}</p>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Elenco fatture</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {invoiceErrors.length > 0 ? (
              <FlashBanner tone="error" message="Le fatture non sono disponibili ora. Riprova tra poco." />
            ) : null}

            <div className="grid gap-3 md:grid-cols-3">
              <div className="rounded-md border border-border/70 bg-muted/30 p-3">
                <p className="text-xs text-muted-foreground">Fatture</p>
                <p className="text-lg font-semibold text-foreground">{invoicesSummary.count}</p>
              </div>
              <div className="rounded-md border border-border/70 bg-muted/30 p-3">
                <p className="text-xs text-muted-foreground">Totale pagato</p>
                <p className="text-lg font-semibold text-foreground">
                  {formatCurrency(invoicesSummary.totalPaid)}
                </p>
              </div>
              <div className="rounded-md border border-border/70 bg-muted/30 p-3">
                <p className="text-xs text-muted-foreground">Da pagare</p>
                <p className="text-lg font-semibold text-foreground">
                  {formatCurrency(invoicesSummary.toPay)}
                </p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fattura</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead className="text-right">Importo</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoiceRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                        Qui troverai le fatture appena disponibili.
                      </TableCell>
                    </TableRow>
                  ) : null}
                  {invoiceRows.map((invoice) => {
                    const action = invoiceAction(invoice);
                    const amount = invoiceDisplayAmount(invoice);
                    const periodHint = invoicePeriodHint(invoice);

                    return (
                      <TableRow key={invoice.id}>
                        <TableCell className="font-medium">
                          <p className="text-foreground">{invoice.number || invoice.id}</p>
                          {periodHint ? (
                            <p className="mt-1 text-xs text-muted-foreground">{periodHint}</p>
                          ) : null}
                        </TableCell>
                        <TableCell>{formatDate(invoice.created)}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className={invoiceStatusClassName(invoice.status)}>
                            {invoiceStatusLabel(invoice.status)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <p className="font-medium">{formatCurrency(amount.value, invoice.currency)}</p>
                          <p className="text-xs text-muted-foreground">{amount.hint}</p>
                        </TableCell>
                        <TableCell className="text-right">
                          {action ? (
                            <a href={action.url} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline">
                              {action.label}
                            </a>
                          ) : (
                            <span className="text-sm text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {hasMore ? (
              <div className="flex justify-end">
                <Button type="button" variant="outline" onClick={loadMoreInvoices} disabled={isLoadingMore}>
                  {isLoadingMore ? 'Caricamento...' : 'Carica altre fatture'}
                </Button>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
